import { useTranslation } from "react-i18next";
import style from "./infoSlab.module.css";
import { createStyles, Progress, Box, Text, Group, SimpleGrid, rem, Badge, Flex, Stack } from "@mantine/core";

const useStyles = createStyles((theme) => ({
    progressLabel: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        lineHeight: 1,
        fontSize: "16px",
        fontWeight: "500",
    },
    progressLabelSmall: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        lineHeight: 1,
        fontSize: "13px",
        fontWeight: "500",
    },

    stat: {
        borderBottom: `${rem(3)} solid`,
        paddingBottom: rem(5),
    },

    statCount: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        lineHeight: 1.3,
    },

    diff: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        display: "flex",
        alignItems: "center",
    },

    icon: {
        color: theme.colorScheme === "dark" ? theme.colors.dark[3] : theme.colors.gray[4],
    },
}));

const InfoSlab = ({ scan, aiScore, displayExplainations, smallVersion = false }) => {
    const { t } = useTranslation(["common"]);

    const { classes } = useStyles();

    const upperTreshold = scan?.version === "2.1" ? 0.9999 : 0.9995;

    // if (aiScore < 0.999) return null

    let probabilityOfCorrectEvaluationUnrounded = 0;
    if (aiScore >= upperTreshold) probabilityOfCorrectEvaluationUnrounded = 0.9999;

    //Regression model 0.9 < x < 0.9995
    const a = 0.9731173590718;
    const b = 34.3321921960834;

    if (aiScore >= 0.9 && aiScore < upperTreshold) probabilityOfCorrectEvaluationUnrounded = a * Math.pow(aiScore, b);

    let probabilityOfCorrectEvaluation = scan?.aiDetectionStrictMode
        ? Math.round(aiScore * 10000) / 100
        : Math.round(probabilityOfCorrectEvaluationUnrounded * 10000) / 100;

    if (probabilityOfCorrectEvaluation === 100) probabilityOfCorrectEvaluation = 99.99;
    if (probabilityOfCorrectEvaluation === 0) probabilityOfCorrectEvaluation = 0.1;

    const segments = [
        {
            value: 100 - probabilityOfCorrectEvaluation,
            color: "var(--green)",
            label: 100 - probabilityOfCorrectEvaluation > 20 ? `${Math.ceil((100 - probabilityOfCorrectEvaluation) * 10) / 10}%` : undefined,
        },
        {
            value: probabilityOfCorrectEvaluation,
            color: "var(--red)",
            label: probabilityOfCorrectEvaluation > 20 ? `${Math.floor(probabilityOfCorrectEvaluation * 10) / 10}%` : undefined,
        },
    ];

    const getAIScore = () => {
        return Math.round((Math.pow((aiScore - 0.0001) * 100, 2) / 100) * 100) / 100;
    };

    const getBorderColor = () => {
        if (scan?.aiDetectionStrictMode) {
            if (aiScore >= 0.9) return smallVersion ? style.borderRedSmall : style.borderRed;
            if (aiScore >= 0.5) return smallVersion ? style.borderYellowSmall : style.borderYellow;
            return smallVersion ? style.borderGreenSmall : style.borderGreen;
        } else {
            if (aiScore >= 0.9995) return smallVersion ? style.borderRedSmall : style.borderRed;
            if (aiScore >= 0.983) return smallVersion ? style.borderYellowSmall : style.borderYellow;
            return smallVersion ? style.borderGreenSmall : style.borderGreen;
        }
    };

    return (
        <div className={`${smallVersion ? style.commentSmall : style.comment} ${getBorderColor()}`}>
            <Group position="apart" mb={smallVersion ? "0.5rem" : "0rem"}>
                <Flex align="center" spacing="xs" justify={"space-between"} w={"100%"} wrap={"wrap"}>
                    {scan?.aiDetectionStrictMode ? (
                        <Text fz={smallVersion ? "md" : "xl"} fw={700}>
                            {aiScore >= 0.9
                                ? t("scan.result.title.ai")
                                : aiScore >= 0.5
                                ? t("scan.result.title.unsure")
                                : aiScore >= 0.1
                                ? t("scan.result.title.probably-original")
                                : t("scan.result.title.original")}
                        </Text>
                    ) : (
                        <Text fz={smallVersion ? "md" : "xl"} fw={700}>
                            {aiScore >= upperTreshold
                                ? t("scan.result.title.ai")
                                : aiScore >= 0.999 && scan?.version !== "2.1"
                                ? t("scan.result.title.sus")
                                : aiScore >= 0.983
                                ? t("scan.result.title.unsure")
                                : aiScore >= 0.9
                                ? t("scan.result.title.probably-original")
                                : t("scan.result.title.original")}
                        </Text>
                    )}

                    {scan?.aiDetectionStrictMode && (
                        <Badge radius={"xs"} color="indigo" mb={"0.25rem"} mt={"0.5rem"}>
                            {t("scan.result.description.strict-mode")}
                        </Badge>
                    )}
                </Flex>
            </Group>

            {!displayExplainations && (
                <div>
                    <Text fz="sm">
                        {`${t("scan.result.description.intro")} ${probabilityOfCorrectEvaluation}% ${t("scan.result.description.explaination")}`}
                    </Text>

                    {aiScore > (scan?.aiDetectionStrictMode ? 0.9 : 0.999) && (
                        <>
                            <br />
                            <Text fz="sm">{t("scan.result.description.ai")}</Text>
                        </>
                    )}
                </div>
            )}
            
            <Progress
                sections={segments}
                size={smallVersion ? 24 : 34}
                classNames={{ label: smallVersion ? classes.progressLabelSmall : classes.progressLabel }}
                mt={!displayExplainations ? 40 : 10}
                aria-hidden="true"
            />
            <SimpleGrid cols={2} mt="xl">
                <Box key={"Orginal"} sx={{ borderBottomColor: "#0BB779" }} className={classes.stat}>
                    <Stack position="apart" align="flex-start" spacing={0}>
                        <Text fz={smallVersion ? "sm" : "md"} fw={600}>
                            {t("scan.result.human")}
                        </Text>
                        <Text c={"var(--green-dark)"} fw={700} fz={smallVersion ? "xs" : "sm"}>
                            {Math.ceil((100 - probabilityOfCorrectEvaluation) * 10) / 10}%
                        </Text>
                    </Stack>
                </Box>

                <Box key={"Kunstig intelligens"} sx={{ borderBottomColor: "#E22024" }} className={classes.stat}>
                    <Stack position="apart" align="flex-end" spacing={0}>
                        <Text fz={smallVersion ? "sm" : "md"} fw={600}>
                            {t("scan.result.ai")}
                        </Text>
                        <Text c={"var(--red)"} fw={700} fz={smallVersion ? "xs" : "sm"}>
                            {Math.floor(probabilityOfCorrectEvaluation * 10) / 10}%
                        </Text>
                    </Stack>
                </Box>
            </SimpleGrid>
        </div>
    );
};

export default InfoSlab;
